import { useGetBriefs } from "@entities/brief/lib/hooks/useGetBriefs";
import { useCancelPayoutExport } from "@entities/campaigns/lib/hooks/useCancelPayoutExport";
import { useCancelVendorPayment } from "@entities/campaigns/lib/hooks/useCancelVendorPayment";
import { useCreatePayoutExport } from "@entities/campaigns/lib/hooks/useCreatePayoutExport";
import { useGetPayoutExports } from "@entities/campaigns/lib/hooks/useGetPayoutExports";
import { useGetPayoutFacets } from "@entities/campaigns/lib/hooks/useGetPayoutFacets";
import { useGetPayouts } from "@entities/campaigns/lib/hooks/useGetPayouts";
import { useParsePayoutExport } from "@entities/campaigns/lib/hooks/useParsePayoutExport";
import { useSyncPayoutExport } from "@entities/campaigns/lib/hooks/useSyncPayoutExport";
import { useDownloadPayoutsQueue } from "@features/downloadPayoutsQueue/useDownloadPayoutsQueue";
import { ToastService } from "@shared/lib/services";
import { FirebaseService } from "@shared/lib/services/firebase.service";
import {
  BankExportStatus,
  IParseExportPaymentsResponse,
  IPayoutInfluencer,
} from "@shared/lib/types/briefCampains";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "use-debounce";
import { toSyncExportRequest } from "../lib/parseExportRows";
import { getVendorPaymentId } from "../lib/payoutDisplay";
import {
  ALL_EXTERNAL_PAYOUT_STATUSES,
  getPayoutsTabFilters,
  PayoutsTab,
} from "../lib/payoutTabs";

const POLL_EXPORT_STATUSES = [
  BankExportStatus.PendingUpload,
  BankExportStatus.ExportFailed,
  BankExportStatus.PendingExport,
];

export function usePayoutsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebounce(searchQuery, 400);
  const [briefSearch, setBriefSearch] = useState("");
  const [debouncedBriefSearch] = useDebounce(briefSearch, 300);
  const [selectedBriefIds, setSelectedBriefIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<PayoutsTab>(PayoutsTab.All);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isPayOpen, setIsPayOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [parsePreview, setParsePreview] = useState<{
    exportId: string;
    fileName: string;
    rows: IParseExportPaymentsResponse[];
  } | null>(null);
  const uploadTargetRef = useRef<{ exportId: string; fileName: string } | null>(
    null
  );

  const { briefs, isFetching: areBriefsLoading } = useGetBriefs({
    pageNo: 1,
    limit: 20,
    searchQuery: debouncedBriefSearch || undefined,
  });

  const briefOptions = useMemo(
    () => briefs.map((brief) => ({ label: brief.name, value: brief._id })),
    [briefs]
  );

  const tabFilters = getPayoutsTabFilters(activeTab);
  const payoutsRequest = {
    pageNo,
    limit: pageSize,
    searchQuery: debouncedSearch,
    externalPayoutStatuses: tabFilters.externalPayoutStatuses,
    isBankVerificationRequired: true as const,
    briefIds: selectedBriefIds,
  };
  const facetsRequest = {
    pageNo: 1,
    limit: 1,
    searchQuery: debouncedSearch,
    externalPayoutStatuses: ALL_EXTERNAL_PAYOUT_STATUSES,
    isBankVerificationRequired: true as const,
  };

  const { payouts, isLoading } = useGetPayouts(payoutsRequest);
  const { facets } = useGetPayoutFacets(facetsRequest);
  const { exports } = useGetPayoutExports({
    pageNo: 1,
    limit: 50,
    statuses: POLL_EXPORT_STATUSES,
  });
  const { createPayoutExport, isCreating, isCreated } = useCreatePayoutExport();
  const { cancelVendorPayment, isCancelling } = useCancelVendorPayment();
  const { cancelPayoutExport, isCancelling: isCancellingExport } =
    useCancelPayoutExport();
  const { parsePayoutExport, parsedRows, isParsing, isParsed } =
    useParsePayoutExport();
  const { syncPayoutExport, isSyncing, isSynced } = useSyncPayoutExport();
  const { downloadPayouts, isDownloading } = useDownloadPayoutsQueue();

  const selectedPayouts = useMemo(
    () =>
      (payouts?.data ?? []).filter((row) =>
        selectedRowKeys.includes(getVendorPaymentId(row))
      ),
    [payouts?.data, selectedRowKeys]
  );

  useEffect(() => {
    setPageNo(1);
    setSelectedRowKeys([]);
  }, [pageSize, debouncedSearch, activeTab, selectedBriefIds]);

  useEffect(() => {
    if (!isCreated) return;
    setIsPayOpen(false);
    setSelectedRowKeys([]);
    setActiveTab(PayoutsTab.Processing);
  }, [isCreated]);

  useEffect(() => {
    if (!isParsed || !uploadTargetRef.current) return;
    setParsePreview({
      exportId: uploadTargetRef.current.exportId,
      fileName: uploadTargetRef.current.fileName,
      rows: parsedRows,
    });
  }, [isParsed, parsedRows]);

  useEffect(() => {
    if (!isSynced) return;
    setParsePreview(null);
  }, [isSynced]);

  const handleUpload = async (exportId: string, file: File) => {
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
    if (ext !== ".csv") {
      ToastService.error("Please upload a .csv file");
      return;
    }
    uploadTargetRef.current = { exportId, fileName: file.name };
    setIsUploading(true);
    try {
      const csv = await FirebaseService.uploadFileToFirebase(
        `payouts/bank-verification-exports/${exportId}`,
        file
      );
      parsePayoutExport({ vendorPaymentBankExportId: exportId, csv });
    } catch {
      ToastService.error("File upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  return {
    searchQuery,
    setSearchQuery,
    briefSearch,
    setBriefSearch,
    selectedBriefIds,
    setSelectedBriefIds,
    briefOptions,
    areBriefsLoading,
    activeTab,
    setActiveTab,
    pageNo,
    pageSize,
    setPageNo,
    setPageSize,
    selectedRowKeys,
    setSelectedRowKeys,
    isPayOpen,
    setIsPayOpen,
    parsePreview,
    setParsePreview,
    payouts,
    isLoading,
    facets,
    exports,
    selectedPayouts,
    isCreating,
    isCancelling,
    isCancellingExport,
    isUploading: isUploading || isParsing,
    isSyncing,
    isDownloading,
    cancelVendorPayment,
    cancelPayoutExport,
    handleUpload,
    downloadPayouts: () =>
      downloadPayouts({
        externalPayoutStatuses: tabFilters.externalPayoutStatuses,
        briefIds: selectedBriefIds,
      }),
    confirmPay: () =>
      createPayoutExport({
        vendorPaymentIds: selectedPayouts.map(getVendorPaymentId),
      }),
    confirmParse: () => {
      if (!parsePreview) return;
      syncPayoutExport(
        toSyncExportRequest(parsePreview.exportId, parsePreview.rows)
      );
    },
    getRowKey: (record: IPayoutInfluencer) => getVendorPaymentId(record),
  };
}
